import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type { McpTokenRegenerateResponse, McpTokenStatus } from './mcp-tokens-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL + '/api/mcp-token/';

/**
 * Shape of the rejection payload produced by every function in this module so
 * callers (hooks, UI components, error boundaries) can read a stable
 * `message` and `status` regardless of which backend response shape was
 * returned.
 */
interface McpTokenClientError {
  message: string;
  status: number | null;
  error: unknown;
}

/**
 * Build a stable rejection object from an axios error.
 *
 * @param {unknown} error - The thrown axios error
 * @param {string} fallbackMessage - Human-readable description used when the
 *   server response has no `message` field
 * @returns {McpTokenClientError} A normalized error payload
 */
function buildClientError(error: unknown, fallbackMessage: string): McpTokenClientError {
  const axiosError = error as {
    response?: { status?: number; data?: Record<string, unknown> };
    message?: string;
  };
  const responseData = axiosError?.response?.data ?? null;
  const serverMessage =
    typeof responseData === 'object' && responseData !== null
      ? typeof responseData.message === 'string'
        ? responseData.message
        : typeof responseData.Error === 'string'
          ? responseData.Error
          : null
      : null;
  return {
    message: serverMessage ?? axiosError?.message ?? fallbackMessage,
    status: axiosError?.response?.status ?? null,
    error: responseData,
  };
}

/**
 * Fetches the status of the authenticated user's MCP token.
 *
 * @returns {Promise<McpTokenStatus>} Status payload describing whether a token
 *   is set and (if applicable) its expiry.
 */
function getMcpTokenStatus(): Promise<McpTokenStatus> {
  return axiosInstance
    .get<McpTokenStatus>(baseURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject(buildClientError(error, 'Failed to fetch MCP token status.')));
}

/**
 * Generates a fresh MCP token for the authenticated user. Any previously
 * issued token is invalidated. The raw token is included in the response
 * exactly once and cannot be retrieved again — callers must surface it
 * immediately and warn the user that it will not be shown again.
 *
 * @returns {Promise<McpTokenRegenerateResponse>} The raw token plus metadata.
 */
function regenerateMcpToken(): Promise<McpTokenRegenerateResponse> {
  return axiosInstance
    .post<McpTokenRegenerateResponse>(baseURL + 'regenerate/')
    .then((response) => response.data)
    .catch((error) => Promise.reject(buildClientError(error, 'Failed to regenerate MCP token.')));
}

/**
 * Revokes the authenticated user's MCP token. After this call, the previously
 * issued token will no longer authenticate via the X-MCP-Token header.
 *
 * @returns {Promise<void>} Resolves once the server confirms revocation.
 */
async function revokeMcpToken(): Promise<void> {
  await axiosInstance
    .delete(baseURL)
    .catch((error) => Promise.reject(buildClientError(error, 'Failed to revoke MCP token.')));
}

export { getMcpTokenStatus, regenerateMcpToken, revokeMcpToken };
